import { Box, Snackbar, Alert } from '@mui/material';
import { FormTitleBox, LoadingSkeleton, ConfirmationDialog } from '../../../common/components';
import ProjectsDataTable from './components/ProjectsDataTable';
import ProjectsPagination from './components/ProjectsPagination';
import ProjectDialog from './components/ProjectDialog';
import ProjectProgressDialog from './components/ProjectProgressDialog';
import ProjectQualityDialog from './components/ProjectQualityDialog';
import ProjectStatsCards from './components/ProjectStatsCards';
import AssignTemplatesDialog from './components/AssignTemplatesDialog';
import { useProjectManagement } from './hooks/useProjectManagement';
import AddIcon from '@mui/icons-material/Add';

/**
 * Dashboard de gestión de proyectos
 * Migrado desde DocAssemble siguiendo los patrones del proyecto
 */
const ProjectManagementDashboard = () => {
  const {
    // Data principal
    projects,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,

    // Estados de diálogos
    dialogOpen,
    progressDialogOpen,
    qualityDialogOpen,
    templatesDialogOpen,
    editMode,
    selectedProject,

    // Datos de soporte
    subscribers,
    taskTemplates,
    progressTemplates,
    qualityTemplates,

    // Estadísticas del dashboard
    projectStats,

    // Funciones de manejo
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleCloseProgressDialog,
    handleCloseQualityDialog,
    handleCloseTemplatesDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleStatusChange,
    handleConstructionTypeChange,
    handleViewProgress,
    handleViewQuality,
    handleAssignTemplates,
    handleAssignTemplatesConfirm,

    // Diálogos de confirmación
    deleteConfirmOpen,
    projectToDelete,
    handleConfirmDelete,
    handleCancelDelete,

    // Notificaciones
    snackbar,
    handleCloseSnackbar,
  } = useProjectManagement();

  return (
    <Box>
      <FormTitleBox
        title="Gestión de Proyectos | Proyectos"
        showButton={true}
        buttonText="Nuevo Proyecto"
        buttonIcon={AddIcon}
        onButtonClick={handleOpenDialog}
        showSearch={true}
        searchLabel="Buscar proyecto"
        searchValue={searchTerm}
        onSearchChange={handleSearch}
        searchSize="medium"
      />

      <ProjectStatsCards stats={projectStats} isLoading={isLoading} />

      <ProjectsDataTable
        data={projects}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onStatusChange={handleStatusChange}
        onConstructionTypeChange={handleConstructionTypeChange}
        onViewProgress={handleViewProgress}
        onViewQuality={handleViewQuality}
        onAssignTemplates={handleAssignTemplates}
        subscribers={subscribers}
        LoadingComponent={LoadingSkeleton}
      />

      <ProjectsPagination
        totalPages={totalPages}
        totalCount={totalCount}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      <ProjectDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        editMode={editMode}
        selectedProject={selectedProject}
        onInputChange={handleInputChange}
        onSelectChange={handleSelectChange}
        onSave={handleSave}
        subscribers={subscribers}
        progressTemplates={progressTemplates}
        qualityTemplates={qualityTemplates}
      />
      <ProjectProgressDialog
        open={progressDialogOpen}
        onClose={handleCloseProgressDialog}
        project={selectedProject}
        progressTemplates={progressTemplates}
      />

      <ProjectQualityDialog
        open={qualityDialogOpen}
        onClose={handleCloseQualityDialog}
        project={selectedProject}
        qualityTemplates={qualityTemplates}
      />

      <AssignTemplatesDialog
        open={templatesDialogOpen}
        onClose={handleCloseTemplatesDialog}
        project={selectedProject}
        taskTemplates={taskTemplates}
        onAssign={handleAssignTemplatesConfirm}
      />

      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        entityName="proyecto"
        message={`¿Está seguro que quiere eliminar el proyecto "${projectToDelete?.name}" de forma permanente?`}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectManagementDashboard;
